import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentChartBarIcon, Cog8ToothIcon } from '@heroicons/react/24/outline';
import { ROUTES } from '../../consts/general';
import ReportTemplate from '../../components/reports/ReportTemplate';
import DateRangePicker from '../../components/reports/DateRangePicker';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ReportEmailSettingsSidebar } from '../../components/reports/ReportEmailSettingsSidebar';
import { MONTHLY_AP_PO_PAID } from '../../consts/reports';
import { MONTHLY_AP_PO_PAID_TYPE } from '../../consts/cronTypes';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import {
  getReportEmailSettings,
  updateReportEmailSettings,
  sendReportPreviewEmail,
} from '../../slices/reportEmailSettings';
import { getReportSendHistory } from '../../slices/reportSendHistory';
import { updateCronInformation } from '../../slices/cronSlice';
import { buildCronInterval } from '../../helpers/cronExpression';
import { toast } from 'react-toastify';
import useDateRangeParams from '../../hooks/useDateRangeParams';

const DEFAULT_END_DATE = new Date().toISOString().split('T')[0];
const DEFAULT_START_DATE = (() => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date.toISOString().split('T')[0];
})();

const PaymentReport = ({
  marginFromSidebar
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { settings: allSettings, loading: isLoadingEmailSettings } = useSelector((s) => s.reportEmailSettings);
  const emailSettings = allSettings['monthly-ap-po-paid'] || {};
  const { sendHistory: allSendHistory, loading: isLoadingSendHistory } = useSelector((s) => s.reportSendHistory);
  const sendHistory = allSendHistory['monthly-ap-po-paid'] || [];
  
  // State management
  const [reportData, setReportData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 50;

  // Date filter state
  const { startDate, endDate, setDateRange } = useDateRangeParams(DEFAULT_START_DATE, DEFAULT_END_DATE);
  
  // Sorting state
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  // Total unique POs state
  const [totalUniquePOs, setTotalUniquePOs] = useState(0);

  // Total POs state
  const [totalPOs, setTotalPOs] = useState(0);
  const [isOpenSettings, setIsOpenSettings] = useState(false);

  // Summary totals state (from backend)
  const [summaryTotals, setSummaryTotals] = useState({
    totalDeposit1: '$0.00',
    totalDeposit2: '$0.00',
    totalBalance: '$0.00',
    totalCredit: '$0.00',
    totalShipping: '$0.00',
    totalAmount: '$0.00'
  });

  // Track the number of unique POs rendered on each page
  const [uniquePOsHistory, setUniquePOsHistory] = useState({});
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);

  // Fetch report metadata on mount
  useEffect(() => {
    fetchReportMetadata();
    dispatch(getReportEmailSettings('monthly-ap-po-paid'));
    dispatch(getReportSendHistory('monthly-ap-po-paid'));
  }, [dispatch]);

  // Fetch data when dates, page, or sorting change
  useEffect(() => {
    if (!initialReady) return;
    if (startDate && endDate) {
      fetchReportData();
    }
  }, [initialReady, startDate, endDate, currentPage, sortField, sortDirection]);

  useEffect(() => {
    setUniquePOsHistory(prev => ({
      ...prev,
      [currentPage]: uniquePOsOnPage.length
    }));
    // eslint-disable-next-line
  }, [reportData, currentPage]);

  const fetchReportMetadata = async () => {
    try {
      const response = await fetch(`/api/v1/reports/${MONTHLY_AP_PO_PAID}/metadata`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch report metadata');
      }

      const data = await response.json();
      setColumns((data.columns || []).map((column, index) => {
        if (index === 0) {
          return {
            ...column,
            sticky: true
          }
        }
        return column
      }));
    } catch (err) {
      console.error('Error fetching metadata:', err);
      setError('Failed to load report configuration');
    }
  };

  const fetchReportData = async () => {
    if (!startDate || !endDate) return;

    const { signal, requestId } = beginRequest();
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        page: currentPage.toString(),
        pageSize: pageSize.toString()
      });

      // Add sorting parameters if present
      if (sortField) {
        params.append('sortField', sortField);
        params.append('sortDirection', sortDirection);
      }

      const response = await fetch(`/api/v1/reports/${MONTHLY_AP_PO_PAID}?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        signal
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch report data');
      }

      const data = await response.json();
      if (!isLatest(requestId)) return;
      setReportData(data.data || []);
      setTotalRecords(data.totalRecords || 0);
      setTotalPages(data.totalPages || 1);
      setTotalUniquePOs(data.totalUniquePOs || 0);
      setTotalPOs(data.totalPOs || 0);
      
      // Set summary totals from backend (across all pages)
      if (data.summary) {
        setSummaryTotals({
          totalDeposit1: data.summary.totalDeposit1 || '$0.00',
          totalDeposit2: data.summary.totalDeposit2 || '$0.00',
          totalBalance: data.summary.totalBalance || '$0.00',
          totalCredit: data.summary.totalCredit || '$0.00',
          totalShipping: data.summary.totalShipping || '$0.00',
          totalAmount: data.summary.totalAmount || '$0.00'
        });
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Error fetching report data:', err);
      if (!isLatest(requestId)) return;
      setError(err.message);
      setReportData([]);
    } finally {
      if (isLatest(requestId)) setIsLoading(false);
    }
  };

  const handleDateChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    setDateRange({ startDate: newStartDate, endDate: newEndDate });
    setCurrentPage(1); // Reset to first page when dates change
    setSortField(null); // Reset sorting when dates change
    setSortDirection(null);
  };

  // Handle sorting changes
  const handleSort = ({ field, direction }) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const handleBackToReports = () => {
    navigate(ROUTES.REPORTS);
  };

  const handleExportCSV = async () => {
    if (!startDate || !endDate) {
      throw new Error('Please select a date range first');
    }

    const params = new URLSearchParams({
      startDate,
      endDate
    });

    // Include sorting in export if present
    if (sortField) {
      params.append('sortField', sortField);
      params.append('sortDirection', sortDirection);
    }

    const response = await fetch(`/api/v1/reports/${MONTHLY_AP_PO_PAID}/export?${params}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Export failed');
    }

    const blob = await response.blob();
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Get filename from response headers or create default
    const contentDisposition = response.headers.get('content-disposition');
    const filename = contentDisposition 
      ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
      : `payment-report_${new Date().toISOString().slice(0, 10)}.csv`;
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  };

  // Breadcrumbs configuration
  const breadcrumbs = [
    {
      label: 'Reports',
      icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" />
    },
    {
      label: ''
    }
  ];

  // Filters component
  const filtersComponent = (
    <DateRangePicker
      startDate={startDate}
      endDate={endDate}
      onDateChange={handleDateChange}
      label="Date Range"
      error={error && error.includes('date') ? error : null}
    />
  );

  // Export button component
  const exportButton = (
    <CSVExportButton
      data={[]} // Empty array since we're using server-side export
      columns={columns}
      filename={`${MONTHLY_AP_PO_PAID}`}
      onExport={handleExportCSV}
      disabled={!startDate || !endDate || isLoading}
      variant="outline"
    />
  );

  // Helper to get unique PO numbers from reportData
  const getUniquePOs = (data) => {
    const set = new Set();
    data.forEach(row => set.add(row.po_number));
    return Array.from(set);
  };

  // Unique POs for current page
  const uniquePOsOnPage = getUniquePOs(reportData);

  // Calculate startPO as the sum of unique POs on all previous pages + 1
  let startPO = 1;
  for (let i = 1; i < currentPage; i++) {
    startPO += uniquePOsHistory[i] || 0;
  }
  const endPO = Math.min(startPO + uniquePOsOnPage.length - 1, totalPOs);

  // Pagination component
  const paginationComponent = totalPages > 1 ? (
    <div className="flex items-center justify-between">
      <div className="text-sm text-gray-700">
        Showing {startPO}-{endPO} of {totalPOs} POs
      </div>
      <div className="flex items-center space-x-2">
        <button
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          Previous
        </button>
        <span className="text-sm text-gray-700">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          Next
        </button>
      </div>
    </div>
  ) : null;

  // Helper to get totals from backend summary (across all pages)
  const calculateTotals = () => {
    return {
      deposit_1: summaryTotals.totalDeposit1,
      deposit_2: summaryTotals.totalDeposit2,
      balance_payment: summaryTotals.totalBalance,
      credit_payment: summaryTotals.totalCredit,
      shipping_payment: summaryTotals.totalShipping,
      total_due: summaryTotals.totalAmount
    };
  };

  // Render table rows grouped by PO, but show total_due only in the first row of each PO group as the sum for that PO
  const renderTableRows = () => {
    // Group by PO
    const grouped = {};
    reportData.forEach(row => {
      if (!grouped[row.po_number]) grouped[row.po_number] = [];
      grouped[row.po_number].push(row);
    });
    const rows = [];
    Object.entries(grouped).forEach(([po, poRows]) => {
      // Sort by date ascending within each PO
      poRows.sort((a, b) => new Date(a.date_paid) - new Date(b.date_paid));
      // Calculate total_due sum for this PO
      const totalDueSum = poRows.reduce((sum, row) => {
        const val = parseFloat((row.total_due || '0').replace(/[$,]/g, '')) || 0;
        return sum + val;
      }, 0);
      const totalDueFormatted = `$${totalDueSum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      poRows.forEach((row, idx) => {
        rows.push(
          <tr key={`${po}-${row.date_paid}-${idx}`} className={idx !== 0 ? 'bg-gray-50' : 'bg-white'}>
            <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${idx !== 0 ? '!bg-gray-50' : 'bg-white'}`}>
              {idx === 0 ? po : ''}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {row.date_paid}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
              {row.deposit_1}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
              {row.deposit_2}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
              {row.balance_payment}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
              {row.credit_payment}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
              {row.shipping_payment}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
              {idx === 0 ? totalDueFormatted : ''}
            </td>
          </tr>
        );
      });
    });
    return rows;
  };

  const handleSuccessSubmit = async (formData) => {
    const payload = {
      emailRecipients: formData?.emailRecipients || [],
      scheduleType: formData?.schedule || null,
      dayOfMonth: formData?.dayOfMonth || null,
      slackChannelId: formData?.slackChannelId || null,
      dayOfWeek: formData?.dayOfWeek || null,
      timeOfDay: formData?.timeOfDay || null,
      emailMessage: formData?.emailMessage || null,
    };
    try {
      await dispatch(updateReportEmailSettings({ reportType: 'monthly-ap-po-paid', ...payload })).unwrap();
      const cronInterval = buildCronInterval({
        scheduleType: payload.scheduleType,
        dayOfMonth: payload.dayOfMonth,
        dayOfWeek: payload.dayOfWeek,
        timeOfDay: payload.timeOfDay,
      });
      if (!cronInterval) {
        toast.error('Invalid schedule settings (cron expression could not be built)', { position: 'bottom-right' });
        return;
      }
      const cronData = {
        id: emailSettings?.cronId,
        type: emailSettings?.cronType || MONTHLY_AP_PO_PAID_TYPE,
        cronInterval,
      };
      await dispatch(updateCronInformation({ singleData: cronData })).unwrap();
      toast.success('Settings saved successfully', { position: 'bottom-right' });
      setIsOpenSettings(false);
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to save settings', { position: 'bottom-right' });
    }
  };

  const sendPreviewEmail = async (formData) => {
    try {
      const payload = {
        email: formData?.emailSendPreviewTo,
        emailMessage: formData?.emailMessage || '',
        type: formData?.type || null,
        startDate: startDate,
        endDate: endDate,
        sortField: sortField || null,
        sortDirection: sortDirection || null,
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'monthly-ap-po-paid', ...payload })).unwrap();
      await dispatch(getReportSendHistory('monthly-ap-po-paid')).unwrap();
      toast.success('Preview email sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send preview', { position: 'bottom-right' });
    }
  };

  const sendPreviewSlack = async (formData) => {
    try {
      const payload = {
        slackChannelId: formData?.slackChannelId,
        emailMessage: formData?.emailMessage || '',
        type: formData?.type || null,
        startDate: startDate,
        endDate: endDate,
        sortField: sortField || null,
        sortDirection: sortDirection || null,
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'monthly-ap-po-paid', ...payload })).unwrap();
      await dispatch(getReportSendHistory('monthly-ap-po-paid')).unwrap();
      toast.success('Slack preview sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send slack preview', { position: 'bottom-right' });
    }
  };

  // Render total row at the top
  const renderTotalRow = () => {
    const totals = calculateTotals();
    return (
      <tr className="bg-gray-200 font-bold">
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"><div/></td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 bg-white"><div/></td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{totals.deposit_1}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{totals.deposit_2}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{totals.balance_payment}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{totals.credit_payment}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{totals.shipping_payment}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">{totals.total_due}</td>
      </tr>
    );
  };

  return (
    <>
      <ReportTemplate
        reportId={MONTHLY_AP_PO_PAID}
        description="Payment details by Purchase Order including deposits, balance, credits, and shipping payments from the accounts payable system"
        breadcrumbs={breadcrumbs}
        marginFromSidebar={marginFromSidebar}
        onBack={handleBackToReports}
        isLoading={isLoading}
        error={error}
        filters={filtersComponent}
        exportButton={
          <div className="flex items-center space-x-2">
            {exportButton}
            <div>
              <Cog8ToothIcon
                className="h-5 w-5 text-primary cursor-pointer"
                onClick={() => {
                  setIsOpenSettings(true);
                }}
              />
            </div>
          </div>
        }
        data={reportData}
        cronType={emailSettings?.cronType || null}
        columns={columns}
        pagination={paginationComponent}
        emptyMessage="No payment data found for the selected date range. Try selecting a different date range or check if there are payments in the system."
        onSort={handleSort}
        sortField={sortField}
        sortDirection={sortDirection}
        enableSorting={true}
      >
        {/* Render total row at the top */}
        {renderTotalRow()}
        {renderTableRows()}
      </ReportTemplate>
      <ReportEmailSettingsSidebar
        reportType={'monthly-ap-po-paid'}
        isOpen={isOpenSettings}
        marginFromSidebar={marginFromSidebar}
        onClose={() => setIsOpenSettings(false)}
        isLoading={isLoadingEmailSettings}
        onSuccessSubmit={handleSuccessSubmit}
        sendPreviewEmail={sendPreviewEmail}
        sendPreviewSlack={sendPreviewSlack}
        defaultSettings={emailSettings || {}}
        sendHistory={sendHistory}
        isLoadingSendHistory={isLoadingSendHistory}
        downloadFilenamePrefix="monthly-ap-po-paid"
      />
    </>
  );
};

export default PaymentReport; 